import { useEffect, useState } from 'react';
import UserDataService from '../../../services/user-data.service';
import { AppUser, UserListInput } from '../../../types/user.types';
import { map } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { getLocateDate } from '../../../utils/format-date-time.utils';
import DateRangeFilter, { DateRangeType } from '../../common/DateRangeFilter';
import OrganisationsService from '../../../services/organisation.service';

const OrgUsers = () => {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<AppUser[]>([]);
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { orgId } = useParams();
  const [dates, setDates] = useState<DateRangeType>({
    startDate: undefined,
    endDate: undefined,
  });
  const navigate = useNavigate();
  const limit = 20;

  const getUserList = async (isFetchMore?: boolean) => {
    try {
      setLoading(true);
      const input: UserListInput = {
        limit,
        offset: users.length,
        orgId,
      };
      const response = await OrganisationsService.getOrgUsers(input);
      if (response.data.data && response.data.data.users) {
        if (users.length === 0) {
          setTotalUserCount(response.data.data.totalUsers);
        }
        setUsers(
          isFetchMore
            ? [...users, ...response.data.data.users]
            : response.data.data.users
        );
      } else {
        console.log('Error: ', response.data.message);
      }
      setLoading(false);
    } catch (err) {
      console.log('Error: ', err);
      setLoading(false);
    }
  };

  const getFilteredUsers = async () => {
    try {
      setLoading(true);
      const input: UserListInput = {
        limit,
        offset: filteredUsers.length,
        startDate: dates.startDate,
        endDate: dates.endDate,
        orgId
      };
      const response = await OrganisationsService.getOrgUsers(input);
      if (response.data.data && response.data.data.users) {
        setTotalUserCount(response.data.data.totalUsers);
        setFilteredUsers([...filteredUsers, ...response.data.data.users]);
      } else {
        console.log('Error: ', response.data.message);
      }
      setLoading(false);
    } catch (err) {
      console.log('Error: ', err);
      setLoading(false);
    }
  };

  const handleScroll = (e: any) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 50 >=
        e.target.scrollHeight &&
      !loading &&
      userList.length < totalUserCount
    ) {
      if (filteredUsers.length > 0) {
        getFilteredUsers();
      } else {
        getUserList(true);
      }
    }
  };

  useEffect(() => {
    getUserList();
  }, []);

  useEffect(() => {
    if (!dates.startDate || !dates.endDate) return;
    getFilteredUsers();
  }, [dates]);

  const userList: AppUser[] = filteredUsers.length > 0 ? filteredUsers : users;

  return (
    <div className="overflow-y-auto w-full p-5">
      <div className="flex justify-between items-center mb-8 z-[2] relative">
        <h3 className="font-normal text-[40px]">Users</h3>
        <DateRangeFilter
          setDateRange={(values) => setDates(values)}
          dateRange={dates}
        />
      </div>
      <div
        className="overflow-auto p-5 rounded-2xl shadow-[0_0_8px_#cbcbcb] max-h-[calc(100%-100px)] overflow-y-auto"
        onScroll={handleScroll}
      >
        <table className="text-[14px] bg-white min-w-full">
          <thead className="">
            <tr className="text-[#616161]">
              <th className="p-3 min-w-[100px] sticky top-0 bg-[white] z-[1]">
                Name
              </th>
              <th className="p-3 min-w-[100px] sticky top-0 bg-[white] z-[1]">
                Email
              </th>
              <th className="p-3 min-w-[100px] sticky top-0 bg-[white] z-[1]">
                Data Retention
              </th>
              <th className="p-3 min-w-[100px] sticky top-0 bg-[white] z-[1]">
                Token Input
              </th>
              <th className="p-3 min-w-[100px] sticky top-0 bg-[white] z-[1]">
                Token Output
              </th>
              <th className="p-3 min-w-[100px] sticky top-0 bg-[white] z-[1]">
                Date Joined
              </th>
              <th className="p-3 min-w-[100px] sticky top-0 bg-[white] z-[1]"></th>
            </tr>
          </thead>
          <tbody className="text-[#333] font-regular">
            {userList &&
              userList.length > 0 &&
              map(userList, (user) => (
                <tr className="static rounded-lg border-b-8 border-white last:border-none text-center">
                  <td className="px-3 py-5 first:font-semibold bg-[#fcfcfc]">{`${user.firstName} ${user.lastName}`}</td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">{user.email}</td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {user?.aiDataRetention ? 'Yes' : 'No'}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {user.totalUsedInputTokens}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {user.totalUsedOutputTokens}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    {user?.createdAt &&
                      getLocateDate(user?.createdAt?.toString())}
                  </td>
                  <td className="px-3 py-5 bg-[#fcfcfc]">
                    <span
                      className="font-bold cursor-pointer"
                      onClick={() =>
                        navigate(`/users/${user._id}`, {
                          state: {
                            name: `${user.firstName} ${user?.lastName}`,
                          },
                        })
                      }
                    >
                      View Details
                    </span>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrgUsers;
